import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, withRouter } from 'react-router-dom';
import Header from 'components/Guest/PreJoin/Header';
import PreJoinLayout from 'components/Guest/PreJoin/PreJoinLayout';
import CookieBanner from 'components/Guest/CookieBanner';
import { SplashLayout } from './SplashLayout';
import { isIosMobile } from 'utils/browser-utils';
import InCallContainer from 'containers/InCall/InCall/InCallContainer';
import { constants as c, hooks } from '@lifesize/clients.sdk';
import usePrevious from 'hooks/usePrevious';
import { callReasons } from 'constants/Guest';
import { navigateToTrialPage } from 'utils/guest-utils';
import CallStatus from './CallStatus';
import ButtonBarRetry from './ButtonBarRetry';
import lsConfig from 'utils/global-config';
import classes from 'styles/core.module.scss';
import { RootState } from 'store/reducers';
import Ringer from './Ringer';
import InCallModalContainer from 'containers/InCallModal/InCallModalContainer';
import { makeStyles } from '@material-ui/core/styles';
import { isCallConnected } from 'utils/call-utils';
import { nanoid } from 'nanoid';
import { Analytics } from '@lifesize/web-clients-core';
import { guestLogin as guestLoginAction } from 'actions/Auth';
import actions from 'actions/Contact';
import { setCallAttempted } from 'reducers/guestFlowSlice';
import { TrackEvents } from 'utils/analytics';
import Logger from 'js-logger';
import LoadingAnimation from 'components/LoadingAnimation/LoadingAnimation';

export interface GuestContainerProps {}

const GuestLandingPage = () => {
  const isGuestFlowEnabled = useSelector((state: RootState) => state?.app?.guestFlow?.isEnabled);
  const bypassSplash = useSelector((state: RootState) => state?.app?.guestFlow?.bypassSplash);
  const call = hooks.useCallState();
  const callState = call.state;
  const callReason = call.reason;
  const previousCallState = usePrevious(call.state, '');
  const callAttempted = useSelector((state: RootState) => state?.app?.guestFlow.callAttempted);
  const isKioskCall = useSelector((state: RootState) => state?.app?.guestFlow.isKiosk);
  const guestFlow = useSelector((state: RootState) => state?.app?.guestFlow);
  const classNames = useStyles();
  const dispatch = useDispatch();
  const addGuestClass = (tagName: string) => {
    const tags = document.getElementsByTagName(tagName);
    if (tags.length) tags[0].classList.add(classes.guest);
  };
  const guestLogin = useCallback((name, extension, email): any => dispatch(guestLoginAction(name, extension, email)), [
    dispatch
  ]);

  // navigate to trial page after successful call and the other end hangs up
  useEffect(() => {
    if (
      previousCallState === c.CALL_STATE_CONNECTED &&
      callState === c.CALL_STATE_DISCONNECTED &&
      callReasons.successful.includes(callReason)
    ) {
      lsConfig.skipOnBeforeUnload();
      if (isKioskCall) {
        window.open('about:blank', '_self');
        window.close();
      } else navigateToTrialPage();
    }
  }, [call, callState, previousCallState, callReason, isKioskCall]);

  useEffect(() => {
    /**
      reload Guest component, when page is loaded from cache.
      this happens specifically in safari,
      when user clicks on back button after navigating to trial page
      user will see retry buttons page instead of pre join form.
    */
    window.onpageshow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    addGuestClass('html');
    addGuestClass('body');
  }, []);

  const initKioskCall = async () => {
    const name = guestFlow?.kioskId;
    const email = '';
    const user = {
      id: `guest-${nanoid()}`,
      role: 'guest',
      email,
      name
    };
    Analytics.identify(user);
    await guestLogin(name, guestFlow?.extension, email);
    await dispatch(actions.startVideoCall(guestFlow?.extension, ''));
    dispatch(setCallAttempted(true));
    Analytics.track(TrackEvents.Call, { extension: guestFlow.extension });
    Logger.info('Call started');
  };

  useEffect(() => {
    !!isKioskCall && initKioskCall();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isGuestFlowEnabled) {
    return <Redirect push to={'/'} />;
  }

  if (isIosMobile() && !bypassSplash) {
    return <SplashLayout />;
  }

  return (
    <>
      {isKioskCall ? (
        <>
          {!isCallConnected(call) ? (
            <div>
              <LoadingAnimation />
            </div>
          ) : (
            <InCallContainer />
          )}
        </>
      ) : callAttempted ? (
        <>
          {!isCallConnected(call) && <CallStatus />}
          {callState === c.CALL_STATE_DISCONNECTED ? <ButtonBarRetry /> : <InCallContainer />}
        </>
      ) : (
        <div className={classNames.preJoinContainer}>
          <Header />
          <PreJoinLayout />
          <CookieBanner />
        </div>
      )}
      <InCallModalContainer />
      <Ringer />
    </>
  );
};

const useStyles = makeStyles(() => ({
  preJoinContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    maxWidth: '100%',
    overflow: 'hidden',
    padding: 0
  }
}));

export default withRouter(GuestLandingPage);
